import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import "./Signup.css";

export default function Signup() {

    const navigate = useNavigate();
    const location = useLocation();

    const params = new URLSearchParams(
        location.search
    );

    const redirect =
        params.get("redirect") ||
        "/rect/app/labs/pricing";

    const plan =
        params.get("plan") || "";


    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        agree: false
    });

    const [loading, setLoading] = useState(false);

    const [googleLoading, setGoogleLoading] =
        useState(false);

    const [error, setError] = useState("");

    const [showPassword, setShowPassword] =
        useState(false);


    /* =========================================================
       FORM CHANGE
    ========================================================= */

    const handleChange = (event) => {

        const {
            name,
            value,
            type,
            checked
        } = event.target;

        setForm(prev => ({
            ...prev,

            [name]:
                type === "checkbox"
                    ? checked
                    : value
        }));

        if (error) {
            setError("");
        }
    };


    /* =========================================================
       VALIDATE LOCAL SIGNUP
    ========================================================= */

    const validate = () => {

        if (!form.firstName.trim()) {
            return "Please enter your first name.";
        }

        if (!form.lastName.trim()) {
            return "Please enter your last name.";
        }

        if (!form.email.trim()) {
            return "Please enter your email address.";
        }

        if (!form.email.includes("@")) {
            return "Please enter a valid email address.";
        }

        if (form.password.length < 8) {
            return "Password must be at least 8 characters.";
        }

        if (
            form.password !==
            form.confirmPassword
        ) {
            return "Passwords do not match.";
        }

        if (!form.agree) {
            return "Please accept the Terms of Service.";
        }

        return null;
    };


    /* =========================================================
       LOCAL SIGNUP
    ========================================================= */

    const handleSubmit = async (event) => {

        event.preventDefault();

        setError("");

        const validationError =
            validate();

        if (validationError) {

            setError(
                validationError
            );

            return;
        }

        setLoading(true);

        try {

            const response = await fetch(
                "/api/auth/signup",
                {
                    method: "POST",

                    headers: {
                        "Content-Type":
                            "application/json"
                    },

                    credentials: "include",

                    body: JSON.stringify({

                        first_name:
                            form.firstName.trim(),

                        last_name:
                            form.lastName.trim(),

                        email:
                            form.email
                                .trim()
                                .toLowerCase(),

                        password:
                            form.password

                    })
                }
            );


            const data =
                await response.json();


            if (!response.ok) {

                throw new Error(
                    data.message ||
                    "Unable to create your account."
                );
            }


            /*
             * Account created and user
             * automatically logged in.
             *
             * Preserve the selected plan.
             */

            let destination =
                redirect;


            if (plan) {

                const separator =
                    destination.includes("?")
                        ? "&"
                        : "?";

                destination =
                    `${destination}` +
                    `${separator}` +
                    `plan=${encodeURIComponent(plan)}`;
            }


            navigate(
                destination,
                {
                    replace: true
                }
            );


        } catch (err) {

            console.error(
                "Signup error:",
                err
            );

            setError(
                err.message ||
                "Something went wrong. Please try again."
            );

        } finally {

            setLoading(false);

        }
    };


    /* =========================================================
       GOOGLE SIGNUP
    ========================================================= */

    const handleGoogleSignup = () => {

        setError("");

        setGoogleLoading(true);


        /*
         * Preserve the user's destination and
         * selected pricing plan.
         */

        const googleParams =
            new URLSearchParams();

        googleParams.set(
            "redirect",
            redirect
        );


        if (plan) {

            googleParams.set(
                "plan",
                plan
            );
        }


        /*
         * Your Flask backend should start
         * the Google OAuth flow.
         *
         * Example:
         *
         * /api/auth/google/login
         *
         * Flask then redirects the user to Google.
         */

        window.location.href = "/api/auth/login";
    };


    return (

        <div className="signup-page">


            {/* =================================================
                LEFT SIDE
            ================================================= */}

            <div className="signup-hero">

                <div className="signup-hero-content">
                    <div className="signup-hero-text">

                        <div className="signup-eyebrow">
                            SPACE ROBOTICS AS A SERVICE
                        </div>

                        <h1>
                            Build the next
                            generation of
                            <span>
                                space robots.
                            </span>
                        </h1>

                        <p>
                            Create your STEMX365 account
                            and launch your first robotics
                            laboratory in minutes.
                        </p>

                    </div>


                    <div className="signup-features">

                        <div className="signup-feature">

                            <div className="feature-icon">
                                🤖
                            </div>

                            <div>
                                <strong>
                                    Space Robots
                                </strong>

                                <span>
                                    Astrobee & Int-Ball2
                                </span>
                            </div>

                        </div>


                        <div className="signup-feature">

                            <div className="feature-icon">
                                💻
                            </div>

                            <div>
                                <strong>
                                    Browser Development
                                </strong>

                                <span>
                                    Code without installing anything
                                </span>
                            </div>

                        </div>


                        <div className="signup-feature">

                            <div className="feature-icon">
                                🎛️
                            </div>

                            <div>
                                <strong>
                                    Mission Control
                                </strong>

                                <span>
                                    Simulate, command and monitor
                                </span>
                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* =================================================
                SIGNUP FORM
            ================================================= */}

            <div className="signup-form-side">

                <div className="signup-form-container">


                    <div className="mobile-logo">

                        <Link
                            to="/"
                            className="signup-logo"
                        >
                            STEMX<span>365</span>
                        </Link>

                    </div>


                    <div className="form-header">

                        <h2>
                            Create your account
                        </h2>

                        <p>
                            Start building your first
                            space robotics lab.
                        </p>

                    </div>


                    {error && (

                        <div className="signup-error">

                            <span>
                                !
                            </span>

                            <div>
                                {error}
                            </div>

                        </div>

                    )}


                    {/* =================================================
                        GOOGLE
                    ================================================= */}

                    <button
                        type="button"
                        className="google-signup-button"
                        onClick={
                            handleGoogleSignup
                        }
                        disabled={
                            googleLoading ||
                            loading
                        }
                    >

                        <span className="google-icon">

                            <svg
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                            >

                                <path
                                    fill="#4285F4"
                                    d="M21.35 12.23c0-.79-.07-1.55-.2-2.28H12v4.32h5.23a4.47 4.47 0 0 1-1.94 2.93v2.44h3.14c1.84-1.69 2.92-4.18 2.92-7.41z"
                                />

                                <path
                                    fill="#34A853"
                                    d="M12 21.5c2.63 0 4.84-.87 6.45-2.36l-3.14-2.44c-.87.58-1.98.92-3.31.92-2.54 0-4.69-1.72-5.46-4.03H3.3v2.52A9.74 9.74 0 0 0 12 21.5z"
                                />

                                <path
                                    fill="#FBBC05"
                                    d="M6.54 13.59A5.86 5.86 0 0 1 6.23 12c0-.55.11-1.08.31-1.59V7.89H3.3A9.5 9.5 0 0 0 2.25 12c0 1.48.35 2.87 1.05 4.11l3.24-2.52z"
                                />

                                <path
                                    fill="#EA4335"
                                    d="M12 6.38c1.43 0 2.71.49 3.72 1.45l2.79-2.79C16.84 3.46 14.63 2.5 12 2.5a9.74 9.74 0 0 0-8.7 5.39l3.24 2.52C7.31 8.1 9.46 6.38 12 6.38z"
                                />

                            </svg>

                        </span>


                        <span>

                            {googleLoading
                                ? "Connecting to Google..."
                                : "Continue with Google"
                            }

                        </span>

                    </button>


                    {/* =================================================
                        DIVIDER
                    ================================================= */}

                    <div className="signup-divider">

                        <span></span>

                        <div>
                            OR
                        </div>

                        <span></span>

                    </div>


                    <form
                        onSubmit={
                            handleSubmit
                        }
                    >


                        {/* NAME */}

                        <div className="name-row">

                            <div className="form-group">

                                <label htmlFor="firstName">
                                    First name
                                </label>

                                <input
                                    id="firstName"
                                    name="firstName"
                                    type="text"
                                    value={
                                        form.firstName
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="First name"
                                    autoComplete="given-name"
                                />

                            </div>


                            <div className="form-group">

                                <label htmlFor="lastName">
                                    Last name
                                </label>

                                <input
                                    id="lastName"
                                    name="lastName"
                                    type="text"
                                    value={
                                        form.lastName
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Last name"
                                    autoComplete="family-name"
                                />

                            </div>

                        </div>


                        {/* EMAIL */}

                        <div className="form-group">

                            <label htmlFor="email">
                                Email address
                            </label>

                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={
                                    form.email
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="you@example.com"
                                autoComplete="email"
                            />

                        </div>


                        {/* PASSWORD */}

                        <div className="form-group">

                            <label htmlFor="password">
                                Password
                            </label>

                            <div className="password-wrapper">

                                <input
                                    id="password"
                                    name="password"
                                    type={
                                        showPassword
                                            ? "text"
                                            : "password"
                                    }
                                    value={
                                        form.password
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="At least 8 characters"
                                    autoComplete="new-password"
                                />

                                <button
                                    type="button"
                                    className="show-password"
                                    onClick={() =>
                                        setShowPassword(
                                            prev =>
                                                !prev
                                        )
                                    }
                                >
                                    {showPassword
                                        ? "Hide"
                                        : "Show"
                                    }
                                </button>

                            </div>

                        </div>


                        {/* CONFIRM PASSWORD */}

                        <div className="form-group">

                            <label htmlFor="confirmPassword">
                                Confirm password
                            </label>

                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                value={
                                    form.confirmPassword
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="Enter your password again"
                                autoComplete="new-password"
                            />

                        </div>


                        {/* TERMS */}

                        <label className="terms">

                            <input
                                type="checkbox"
                                name="agree"
                                checked={
                                    form.agree
                                }
                                onChange={
                                    handleChange
                                }
                            />

                            <span>

                                I agree to the{" "}

                                <Link to="/terms">
                                    Terms of Service
                                </Link>

                                {" "}and{" "}

                                <Link to="/privacy">
                                    Privacy Policy
                                </Link>.

                            </span>

                        </label>


                        {/* SUBMIT */}

                        <button
                            type="submit"
                            className="signup-button"
                            disabled={
                                loading ||
                                googleLoading
                            }
                        >

                            {loading
                                ? "Creating account..."
                                : "Create Account"
                            }

                        </button>


                    </form>


                    <div className="login-link">

                        Already have an account?

                        <Link
                            to={
                                `/login?redirect=` +
                                encodeURIComponent(
                                    redirect
                                ) +
                                (
                                    plan
                                        ? `&plan=${encodeURIComponent(plan)}`
                                        : ""
                                )
                            }
                        >
                            Sign in
                        </Link>

                    </div>


                    <div className="secure-note">

                        🔒 Your account and laboratory
                        data are securely stored.

                    </div>

                </div>

            </div>

        </div>
    );
}